// Models PoE's Unpredictable Recombination (Settlers of Kalguur / 3.25+).
//
// Reference: resources/recombinator-mechanics-3.29.wiki
//
// Mechanics modeled here:
// - Mods from both items are pooled per slot type (prefix/suffix).
// - The final number of mods per slot type is rolled based on the pool size
//   (Table 1 in the wiki), independently for prefixes and suffixes.
// - Mods are then drawn sequentially and uniformly from the remaining
//   eligible pool entries until the rolled count is met or no eligible
//   entries remain. Drawing a mod makes all other entries of the same mod
//   group (same token) ineligible, so duplicate entries collapse.
// - Aspect mods (`pa1`/`sa1`, non-native to both bases) count toward the
//   pool size for the count roll but are never eligible for selection, so
//   slots they would fill go unfilled (padding/blocking).
// - Only one exclusive mod (`pe1`/`se1`) can appear on the output: drawing
//   one removes every other exclusive from both pools, which can leave the
//   rolled counts unfulfilled. Which pool fills first is 50/50 and matters
//   only through this cross-pool exclusive removal.
// - Crafted mods (`pc1`/`sc1`, optionally exclusive via `pce1`/`sce1`) are
//   calculated normally for odds purposes, but (being bench crafts) never
//   transfer to the output. They can therefore be stripped from the final
//   outcomes, merging rows that become identical.
// - Mod labels may be numeric ("p1") or bracketed names ("p[life]"), which
//   unambiguously delimit the label even when it contains p/s letters.

export type ModKind = "normal" | "exclusive" | "aspect";
export type Slot = "prefix" | "suffix";

export interface Mod {
  /** Mod group identity, e.g. "p1". Distinct tokens are distinct groups. */
  id: string;
  slot: Slot;
  kind: ModKind;
  /** Whether this is a bench-crafted modifier (never transfers to the output). */
  crafted: boolean;
}

export interface ParsedInput {
  item1: Mod[];
  item2: Mod[];
}

export interface Outcome {
  prefixes: string[];
  suffixes: string[];
  probability: number;
}

// Wiki table: "Estimated modifier count distribution" (pool size -> chance
// for each final mod count).
const COUNT_DISTRIBUTION: [count: number, probability: number][][] = [
  [[0, 1]], // pool size 0
  [[0, 0.41], [1, 0.59]],
  [[1, 2 / 3], [2, 1 / 3]],
  [[1, 0.4], [2, 0.5], [3, 0.1]],
  [[1, 0.1], [2, 0.6], [3, 0.3]],
  [[2, 0.43], [3, 0.57]],
  [[2, 0.3], [3, 0.7]],
];

const TOKEN_PATTERN = /(p|s)([cea]{0,2})(\d+|\[[^\]\s]+\])/gy;

/**
 * Parses a single mod token such as "p1", "pe2", "pa3", "sc1", "sce4",
 * "p[life]", or "pce[block]".
 *
 * The slot letter ("p"/"s") is followed by optional kind letters
 * ("e" = exclusive, "a" = aspect/non-native to both bases, "c" = crafted)
 * in any order, and a label: a number or a bracketed name ("[life]"). Tokens
 * with the same full text share a mod group; distinct tokens are distinct
 * mod groups.
 */
export function parseToken(token: string): Mod {
  TOKEN_PATTERN.lastIndex = 0;
  const match = TOKEN_PATTERN.exec(token);
  if (!match || match[0] !== token) {
    throw new Error(
      `Invalid modifier token "${token}": expected tokens like p1, pe1, pa1, pc1, pce1, s1, se1, sa1, sc1, sce1, p[life], pce[block]`
    );
  }
  const [, slotLetter, kindLetters] = match;
  let kind: ModKind = "normal";
  let crafted = false;
  for (const letter of kindLetters) {
    if (letter === "c") {
      if (crafted) throw new Error(`Invalid modifier token "${token}": duplicate "c"`);
      crafted = true;
    } else {
      const letterKind: ModKind = letter === "e" ? "exclusive" : "aspect";
      if (kind !== "normal") {
        throw new Error(
          `Invalid modifier token "${token}": a mod cannot be both exclusive and aspect`
        );
      }
      kind = letterKind;
    }
  }
  return {
    id: token,
    slot: slotLetter === "p" ? "prefix" : "suffix",
    kind,
    crafted,
  };
}

export function parseInput(input: string): ParsedInput {
  const parts = input.split("+");
  if (parts.length !== 2) {
    throw new Error(
      "Input must contain exactly two items separated by '+' (e.g. \"p1p2s1 + p1p3s1\")"
    );
  }

  const parseItem = (part: string): Mod[] => {
    const text = part.replace(/\s+/g, "").toLowerCase();
    const mods: Mod[] = [];
    let offset = 0;
    while (offset < text.length) {
      TOKEN_PATTERN.lastIndex = offset;
      const match = TOKEN_PATTERN.exec(text);
      if (!match) {
        throw new Error(
          `Invalid modifier token in "${part.trim()}": expected tokens like p1, pe1, pa1, pc1, pce1, p[life], pce[block], s1, se1, sa1, sc1, sce1 (near "${text.slice(offset)}")`
        );
      }
      offset = TOKEN_PATTERN.lastIndex;
      mods.push(parseToken(match[0]));
    }

    for (const slot of ["prefix", "suffix"] as const) {
      const count = mods.filter((m) => m.slot === slot).length;
      if (count > 3) {
        throw new Error(
          `Item "${part.trim()}" has ${count} ${slot}es; an item can have at most 3`
        );
      }
    }
    return mods;
  };

  return {
    item1: parseItem(parts[0]),
    item2: parseItem(parts[1]),
  };
}

interface PoolSelection {
  /** Ids of the mods visible on the final item. */
  mods: string[];
  /** Whether an exclusive mod was drawn (blocks all other exclusives). */
  exclusivePicked: boolean;
  probability: number;
}

/**
 * Enumerates every possible outcome of drawing `count` mods from `entries`,
 * with the wiki's rules: uniform draws over remaining eligible entries,
 * same-group entries become ineligible after a draw, aspect mods are never
 * eligible, and drawing an exclusive removes all other exclusives.
 *
 * Stops early (leaving the rolled count unfulfilled) when no eligible
 * entries remain.
 */
function enumeratePool(
  entries: Mod[],
  count: number,
  exclusivesBlocked: boolean
): PoolSelection[] {
  const results: PoolSelection[] = [];
  const removed = entries.map(() => false);
  const mods: string[] = [];
  let exclusivePicked = exclusivesBlocked;

  const recurse = (placed: number, branchProb: number) => {
    const emit = () =>
      results.push({ mods: [...mods], exclusivePicked, probability: branchProb });

    if (placed === count) {
      emit();
      return;
    }

    const eligible: number[] = [];
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      if (removed[i] || entry.kind === "aspect") continue;
      if (entry.kind === "exclusive" && exclusivePicked) continue;
      eligible.push(i);
    }
    if (eligible.length === 0) {
      emit();
      return;
    }
    const drawProb = branchProb / eligible.length;

    for (const i of eligible) {
      const entry = entries[i];
      const newlyRemoved: number[] = [];
      const previouslyExclusivePicked = exclusivePicked;

      removed[i] = true;
      newlyRemoved.push(i);
      for (let j = 0; j < entries.length; j++) {
        if (j !== i && !removed[j] && entries[j].id === entry.id) {
          removed[j] = true;
          newlyRemoved.push(j);
        }
      }
      if (entry.kind === "exclusive" && !exclusivePicked) {
        exclusivePicked = true;
        for (let j = 0; j < entries.length; j++) {
          if (!removed[j] && entries[j].kind === "exclusive") {
            removed[j] = true;
            newlyRemoved.push(j);
          }
        }
      }

      mods.push(entry.id);
      recurse(placed + 1, drawProb);
      mods.pop();

      for (const j of newlyRemoved) {
        removed[j] = false;
      }
      exclusivePicked = previouslyExclusivePicked;
    }
  };

  recurse(0, 1);
  return results;
}

/**
 * Calculates the probability of every possible output item for a
 * recombination of the two given items.
 *
 * Returns outcomes sorted by descending probability. Prefix and suffix
 * outcomes are grouped into shared rows; the probabilities within a row are
 * the joint probability of that exact set of prefixes and suffixes.
 */
export function calculateOutcomes(
  input: string | ParsedInput,
  options?: { removeCrafted?: boolean }
): Outcome[] {
  const { item1, item2 } = typeof input === "string" ? parseInput(input) : input;
  const both = [...item1, ...item2];
  const prefixPool = both.filter((m) => m.slot === "prefix");
  const suffixPool = both.filter((m) => m.slot === "suffix");

  const prefixCounts = COUNT_DISTRIBUTION[prefixPool.length];
  const suffixCounts = COUNT_DISTRIBUTION[suffixPool.length];
  if (!prefixCounts || !suffixCounts) {
    throw new Error("Pool size out of range");
  }

  interface Accumulator {
    prefixes: string[];
    suffixes: string[];
    probability: number;
  }
  const byKey = new Map<string, Accumulator>();

  const prefixFirstOrders = [true, false];
  for (const [prefixCount, prefixCountProb] of prefixCounts) {
    for (const [suffixCount, suffixCountProb] of suffixCounts) {
      for (const prefixFirst of prefixFirstOrders) {
        // Which pool fills first is 50/50. It only matters when exclusives
        // are involved (drawing one removes exclusives from both pools).
        const [firstPool, firstCount] = prefixFirst
          ? [prefixPool, prefixCount]
          : [suffixPool, suffixCount];
        const [secondPool, secondCount] = prefixFirst
          ? [suffixPool, suffixCount]
          : [prefixPool, prefixCount];

        for (const first of enumeratePool(firstPool, firstCount, false)) {
          for (const second of enumeratePool(secondPool, secondCount, first.exclusivePicked)) {
            const prefixes = prefixFirst ? first.mods : second.mods;
            const suffixes = prefixFirst ? second.mods : first.mods;
            const key =
              [...prefixes].sort().join(",") + "|" + [...suffixes].sort().join(",");
            const probability =
              0.5 * prefixCountProb * suffixCountProb * first.probability * second.probability;

            let acc = byKey.get(key);
            if (!acc) {
              acc = { prefixes: [...prefixes], suffixes: [...suffixes], probability: 0 };
              byKey.set(key, acc);
            }
            acc.probability += probability;
          }
        }
      }
    }
  }

  let outcomes: Iterable<Accumulator> = byKey.values();
  if (options?.removeCrafted ?? false) {
    // Bench-crafted mods never transfer to the output, so strip them and
    // merge rows that end up with the same mods (summing probabilities).
    const craftedIds = new Set(both.filter((m) => m.crafted).map((m) => m.id));
    const merged = new Map<string, Accumulator>();
    for (const { prefixes, suffixes, probability } of byKey.values()) {
      const keptPrefixes = prefixes.filter((id) => !craftedIds.has(id));
      const keptSuffixes = suffixes.filter((id) => !craftedIds.has(id));
      const key = keptPrefixes.sort().join(",") + "|" + keptSuffixes.sort().join(",");
      const acc = merged.get(key);
      if (acc) {
        acc.probability += probability;
      } else {
        merged.set(key, {
          prefixes: keptPrefixes,
          suffixes: keptSuffixes,
          probability,
        });
      }
    }
    outcomes = merged.values();
  }

  return [...outcomes]
    .map(({ prefixes, suffixes, probability }) => ({
      prefixes: prefixes.sort(),
      suffixes: suffixes.sort(),
      probability,
    }))
    .sort(
      (a, b) =>
        b.probability - a.probability ||
        a.prefixes.join(",").localeCompare(b.prefixes.join(",")) ||
        a.suffixes.join(",").localeCompare(b.suffixes.join(","))
    );
}
